import { chunk } from "lodash-es";

import type { PinBatchedQuery } from "src/analytics/dashboard/dashboardFilterParams";
import { analyticsQueryApi } from "src/api/endpoints";
import type { QueryContext, ResultsValue } from "src/clients/analytics-query";
import { getLocalTimezone } from "src/utils/timezones";

// The query lambda runs a request's queries sequentially and API Gateway caps a
// request at 30s, so a batch is split into parallel requests capped here.
export const MAX_QUERIES_PER_REQUEST = 2;

export const chunkQueries = <T>(
  queries: Record<string, T>,
): Array<Record<string, T>> =>
  chunk(Object.entries(queries), MAX_QUERIES_PER_REQUEST).map((entries) =>
    Object.fromEntries(entries),
  );

type Waiter = {
  resolve: (result: ResultsValue | undefined) => void;
  reject: (error: unknown) => void;
};

type PendingQuery = { id: string; query: PinBatchedQuery; waiters: Waiter[] };

const pendingByTarget = new Map<string, Map<string, PendingQuery>>();

const targetKey = (
  baseUrl: string,
  dataSource: QueryContext | undefined,
): string => JSON.stringify([baseUrl, dataSource]);

// Callers name their slot, but two queries can arrive under one id (a chart
// mid-parameter-change), and a shared slot would hand one of them the other's
// rows — so only the first keeps the bare id.
const slotNames = (pending: PendingQuery[]): string[] =>
  pending.map(({ id }, index) =>
    pending.findIndex((other) => other.id === id) === index
      ? id
      : `${id}#${index}`,
  );

const sendBatch = async (
  baseUrl: string,
  dataSource: QueryContext | undefined,
  pending: PendingQuery[],
): Promise<void> => {
  const slots = slotNames(pending);
  try {
    const { data } = await analyticsQueryApi(
      baseUrl,
    ).querySqlAnalyticsApiV1QueryPost({
      queryRequest: {
        queries: Object.fromEntries(
          pending.map(({ query }, index) => [slots[index], query]),
        ),
        tz: getLocalTimezone(),
        ...(dataSource !== undefined && { context: dataSource }),
      },
    });
    pending.map(({ waiters }, index) =>
      waiters.map((waiter) => waiter.resolve(data.results?.[slots[index]])),
    );
  } catch (error) {
    pending.map(({ waiters }) => waiters.map((waiter) => waiter.reject(error)));
  }
};

const flushTarget = (
  target: string,
  baseUrl: string,
  dataSource: QueryContext | undefined,
): void => {
  const pending = pendingByTarget.get(target);
  if (!pending) return;
  pendingByTarget.delete(target);
  chunk([...pending.values()], MAX_QUERIES_PER_REQUEST).map(
    (group) => void sendBatch(baseUrl, dataSource, group),
  );
};

// Sends the queries enqueued in this tick as requests of at most
// MAX_QUERIES_PER_REQUEST, resolving each caller with its own slice. `id` names
// the caller's slot in the request and response.
export const enqueueMetricsQuery = (
  baseUrl: string,
  dataSource: QueryContext | undefined,
  id: string,
  query: PinBatchedQuery,
): Promise<ResultsValue | undefined> =>
  new Promise((resolve, reject) => {
    const target = targetKey(baseUrl, dataSource);
    const waiter: Waiter = { resolve, reject };
    const queryId = JSON.stringify(query);
    const pending = pendingByTarget.get(target);
    if (!pending) {
      pendingByTarget.set(
        target,
        new Map([[queryId, { id, query, waiters: [waiter] }]]),
      );
      queueMicrotask(() => flushTarget(target, baseUrl, dataSource));
      return;
    }
    const existing = pending.get(queryId);
    pending.set(queryId, {
      id: existing?.id ?? id,
      query,
      waiters: existing ? [...existing.waiters, waiter] : [waiter],
    });
  });
